import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Printer, ArrowLeft } from "lucide-react";
import { api, num, fmtDate } from "../lib/api";
import { Button } from "../components/shared";
import { CompanyLogo } from "../components/CompanyLogo";

const ONES = ["", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine", "Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen", "Seventeen", "Eighteen", "Nineteen"];
const TENS = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];
const two = (n) => (n < 20 ? ONES[n] : `${TENS[Math.floor(n / 10)]} ${ONES[n % 10]}`.trim());
const three = (n) => [n >= 100 ? `${ONES[Math.floor(n / 100)]} Hundred` : "", two(n % 100)].filter(Boolean).join(" ");
export const inWords = (amt) => {
  let n = Math.round(amt || 0);
  if (!n) return "Zero Rupees Only";
  const parts = [];
  [[10000000, "Crore"], [100000, "Lakh"], [1000, "Thousand"]].forEach(([d, w]) => {
    if (n >= d) { const x = Math.floor(n / d); parts.push(`${x < 100 ? two(x) : three(x)} ${w}`); n %= d; }
  });
  if (n) parts.push(three(n));
  return `Rupees ${parts.join(" ")} Only`;
};

const rs = (v) => `${Number(v || 0) < 0 ? "-" : ""}₹${Math.abs(Number(v || 0)).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const TH = "border border-slate-300 px-2 py-1.5";

export default function FabricDocPrint() {
  const { kind, id } = useParams();
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  useEffect(() => { api.get(`/${kind}/${id}`).then((r) => setD(r.data)).catch(() => {}); }, [kind, id]);
  if (!d) return <div className="grid place-items-center h-96"><div className="h-10 w-10 border-4 border-blue-800 border-t-transparent rounded-full animate-spin" /></div>;

  const isSale = kind === "fabric-sales";
  const c = d.company || {};
  const party = isSale
    ? { title: "Bill To", name: d.buyer_name, address: d.buyer_address, gstin: d.buyer_gstin, phone: d.buyer_phone }
    : { title: "Returned To (Supplier)", name: d.supplier_name, address: d.supplier_address, gstin: d.supplier_gstin };
  const meta = isSale
    ? [["Place of Supply", d.place_of_supply], ["Vehicle No", d.vehicle_number], ["Transport", d.transport]]
    : [["Against Bill No", d.ref_bill_number], ["Reason", d.reason], ["Vehicle No", d.vehicle_number], ["Transport", d.transport]];

  return (
    <div className="overflow-x-auto">
      <div className="flex items-center justify-between mb-4 no-print">
        <Button variant="secondary" onClick={() => navigate("/fabric-docs")}><ArrowLeft size={15} /> Back</Button>
        <Button onClick={() => window.print()} data-testid="fdoc-print-btn"><Printer size={15} /> Print / Save PDF</Button>
      </div>
      <div id="challan-print" className="bg-white mx-auto shadow-sm border border-slate-200" style={{ maxWidth: "800px", minWidth: "640px", padding: "40px", fontFamily: "Inter, sans-serif" }} data-testid="fdoc-print-sheet">
        <div className="flex items-start justify-between border-b-2 border-slate-800 pb-4">
          <div className="flex items-center gap-3">
            <CompanyLogo hasLogo={c.has_logo} className="h-14 w-14 rounded-lg bg-white border border-slate-200 p-1" fallback={
              <div className="h-14 w-14 rounded-lg bg-blue-800 text-white grid place-items-center text-2xl font-extrabold">{(c.name || "A").slice(0, 1)}</div>} />
            <div>
              <h1 className="text-2xl font-extrabold text-slate-900">{c.name || "ASV APPARELS"}</h1>
              {c.address && <p className="text-xs text-slate-500 max-w-xs">{c.address}</p>}
              <p className="text-xs text-slate-500">{[c.gstin && `GSTIN: ${c.gstin}`, c.phone1, c.email].filter(Boolean).join(" · ")}</p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-lg font-bold text-slate-800">{isSale ? "TAX INVOICE" : "GOODS RETURN NOTE"}</p>
            <p className="text-xs text-slate-500">{isSale ? "Sale of Fabric" : "Fabric returned to supplier"}</p>
            <p className="text-sm font-mono font-semibold text-blue-800 mt-1" data-testid="fdoc-number">{d.number}</p>
            <p className="text-xs text-slate-500 mt-1">Date: {fmtDate(d.date)}</p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-6 mt-5">
          <div>
            <p className="text-xs font-semibold text-slate-500 uppercase mb-1">{party.title}</p>
            <p className="text-sm font-semibold text-slate-800" data-testid="fdoc-party">{party.name}</p>
            {party.address && <p className="text-xs text-slate-500 whitespace-pre-line">{party.address}</p>}
            {party.gstin && <p className="text-xs text-slate-500">GSTIN: {party.gstin}</p>}
            {party.phone && <p className="text-xs text-slate-500">Phone: {party.phone}</p>}
          </div>
          <div className="text-sm grid grid-cols-2 gap-y-1 h-fit">
            {meta.filter(([, v]) => v).map(([l, v]) => (<React.Fragment key={l}><span className="text-slate-500">{l}</span><span className="font-medium text-slate-800">{v}</span></React.Fragment>))}
          </div>
        </div>

        <table className="w-full mt-5 border border-slate-300 text-sm">
          <thead><tr className="bg-slate-100 text-xs uppercase text-slate-600">
            <th className={`${TH} text-left`}>#</th><th className={`${TH} text-left`}>Lot No</th><th className={`${TH} text-left`}>Fabric / Colour</th>
            {isSale && <th className={`${TH} text-left`}>HSN</th>}
            <th className={`${TH} text-right`}>Rolls</th><th className={`${TH} text-right`}>Qty</th><th className={`${TH} text-right`}>Rate</th><th className={`${TH} text-right`}>Amount</th>
          </tr></thead>
          <tbody>
            {d.lines.map((l, i) => (
              <tr key={i}>
                <td className={TH}>{i + 1}</td><td className={`${TH} font-mono`}>{l.lot_number}</td>
                <td className={TH}>{[l.fabric_type, l.colour].filter(Boolean).join(" · ")}</td>
                {isSale && <td className={TH}>{l.hsn || "—"}</td>}
                <td className={`${TH} text-right`}>{l.rolls || "—"}</td>
                <td className={`${TH} text-right tabular`}>{num(l.qty)} {l.unit}</td>
                <td className={`${TH} text-right tabular`}>{rs(l.rate)}</td>
                <td className={`${TH} text-right tabular`}>{rs(l.amount)}</td>
              </tr>
            ))}
            <tr className="bg-slate-50 font-bold">
              <td className={TH} colSpan={isSale ? 5 : 4}>TOTAL</td>
              <td className={`${TH} text-right tabular`}>{num(d.total_qty)}</td><td className={TH} />
              <td className={`${TH} text-right tabular`} data-testid="fdoc-subtotal">{rs(isSale ? d.taxable_value : d.total_value)}</td>
            </tr>
          </tbody>
        </table>

        {isSale && (
          <div className="flex justify-between gap-6 mt-4">
            <p className="text-sm text-slate-700 self-end"><span className="text-xs text-slate-500 uppercase block">Amount in words</span><b data-testid="fdoc-words">{inWords(d.grand_total)}</b></p>
            <table className="text-sm w-72 border border-slate-300">
              <tbody>
                <tr><td className={TH}>Taxable Value</td><td className={`${TH} text-right tabular`}>{rs(d.taxable_value)}</td></tr>
                {d.gst_type === "IGST"
                  ? <tr><td className={TH}>IGST @ {num(d.gst_rate)}%</td><td className={`${TH} text-right tabular`}>{rs(d.igst)}</td></tr>
                  : <><tr><td className={TH}>CGST @ {num(d.gst_rate / 2)}%</td><td className={`${TH} text-right tabular`}>{rs(d.cgst)}</td></tr>
                    <tr><td className={TH}>SGST @ {num(d.gst_rate / 2)}%</td><td className={`${TH} text-right tabular`}>{rs(d.sgst)}</td></tr></>}
                <tr><td className={TH}>Round Off</td><td className={`${TH} text-right tabular`}>{rs(d.round_off)}</td></tr>
                <tr className="font-bold bg-slate-50"><td className={TH}>Grand Total</td><td className={`${TH} text-right tabular`} data-testid="fdoc-grand-total">{rs(d.grand_total)}</td></tr>
              </tbody>
            </table>
          </div>
        )}

        {d.remarks && <p className="text-sm mt-4 text-slate-600"><span className="font-semibold">Remarks:</span> {d.remarks}</p>}
        <div className="grid grid-cols-3 gap-6 mt-16 text-center text-xs text-slate-600">
          {(isSale ? ["Prepared By", "Receiver's Signature", `For ${c.name || "ASV APPARELS"}`] : ["Prepared By", "Checked By", "Supplier's Signature"]).map((s) => (
            <div key={s}><div className="border-t border-slate-400 pt-1">{s}</div></div>
          ))}
        </div>
        <p className="text-center text-[10px] text-slate-400 mt-6">This is a computer generated document · {c.name || "ASV APPARELS"}</p>
      </div>
    </div>
  );
}
